import { startCase } from 'lodash';

import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { Text } from '@/components/text';
import { RankingInterface } from '@/types';
import { tv } from '@/utils/styles';

import { getSectionOfListAroundItem } from './utils';

interface Props {
  data: { [key: string]: RankingInterface[] };
  description?: string;
  title?: string;
  highlightedItemId: string;
  highlightedItemRank: number;
  maxListLength?: number;
}

const RankingList = ({
  data,
  description,
  title,
  highlightedItemId,
  highlightedItemRank,
  maxListLength = 3,
}: Props) => {
  const { base, heading, descriptionStyles, position, itemName, ol, li, link, highlightedItemRankStyles } = styles();

  const nonEmptyLists = Object.entries(data).filter(([, listWithData]) => listWithData.length > 0);

  return (
    <article className={base()}>
      {title && (
        <Heading variant="h3" className={heading()}>
          <span className={highlightedItemRankStyles()}>#{highlightedItemRank}</span>
          {title}
        </Heading>
      )}
      {description && <Text className={descriptionStyles()} dangerouslySetInnerHTML={{ __html: description }} />}
      <div className="mt-6">
        {nonEmptyLists.map(([key, list], i) => {
          const sectionTitle = startCase(key);
          const isLastList = i === nonEmptyLists.length - 1;

          const sectionOfListToShow = getSectionOfListAroundItem({
            list,
            highlightedItemId,
            maxListLength,
          });

          return (
            <div key={key}>
              <Heading as="h3" variant="h6" className="mb-2">
                {sectionTitle}
              </Heading>
              <ol className={ol({ isLastList })}>
                {sectionOfListToShow.map((item: RankingInterface) => {
                  const isSame = highlightedItemId === item.id;
                  return (
                    <li key={`key-${item.ranking}`} className={li()}>
                      <Link href={item.url} className={link({ isBold: isSame })}>
                        <div className={position()}>#{item.ranking}</div>
                        <Text className={itemName()}>{item.name}</Text>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </div>
          );
        })}
      </div>
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'h-full rounded-md border border-grey-300 p-8 shadow-md',
    heading: 'flex items-center font-semibold leading-none',
    descriptionStyles: 'm-0 text-baseSm',
    position: 'text-sm',
    itemName: 'm-0 truncate text-baseSm leading-snug',
    ol: '-mx-1 mb-6',
    li: 'border-b border-b-grey-200 pl-1 last:border-b-0 hover:bg-grey-50',
    link: 'text-primary grid grid-cols-[40px_1fr] items-center py-1.5 text-base leading-snug hover:[&_div:first-child]:text-text-primary',
    highlightedItemRankStyles: 'mr-2 text-xl',
  },
  variants: {
    isBold: {
      true: {
        link: 'font-semibold',
        position: 'text-text-primary',
      },
    },
    isLastList: {
      true: {
        ol: 'mb-0',
      },
    },
  },
});

export { RankingList };
